import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Alert
} from '@mui/material';
import { Close, Save, Edit, Notes } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import telegramUserService from '../services/telegramUserService';

const UserNoteDialog = ({ open, onClose, user }) => {
    const [note, setNote] = useState('');
    const [originalNote, setOriginalNote] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (open && user) {
            loadUserNote();
        }
    }, [open, user]);

    const loadUserNote = async () => {
        if (!user?.userId) return;
        
        setLoading(true);
        try {
            const response = await telegramUserService.getUserNote(user.userId);
            const noteText = response.note || '';
            setNote(noteText);
            setOriginalNote(noteText);
        } catch (error) {
            console.error('Ошибка при загрузке заметки:', error);
            enqueueSnackbar('Ошибка при загрузке заметки', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!user?.userId) return;
        
        setSaving(true);
        try {
            await telegramUserService.updateUserNote(user.userId, note);
            setOriginalNote(note);
            setEditing(false);
            enqueueSnackbar('Заметка сохранена', { variant: 'success' });
        } catch (error) {
            console.error('Ошибка при сохранении заметки:', error);
            enqueueSnackbar('Ошибка при сохранении заметки', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setNote(originalNote);
        setEditing(false);
    };

    const handleClose = () => {
        if (editing && note !== originalNote) {
            if (window.confirm('У вас есть несохраненные изменения. Закрыть без сохранения?')) {
                setNote(originalNote);
                setEditing(false);
                onClose();
            }
        } else {
            onClose();
        }
    };

    const getUserDisplayName = () => {
        if (!user) return '';
        return user.username 
            ? `@${user.username}` 
            : `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.userId;
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: '400px' }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                <Notes color="primary" />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">
                        Заметка пользователя
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {getUserDisplayName()}
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        {!editing && !note && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                У этого пользователя пока нет заметки
                            </Alert>
                        )}
                        
                        <TextField
                            fullWidth
                            multiline
                            rows={8}
                            variant="outlined"
                            placeholder="Введите заметку о пользователе..."
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            disabled={!editing}
                            inputProps={{ maxLength: 2000 }}
                            helperText={`${note.length}/2000 символов`}
                            sx={{
                                '& .MuiInputBase-input': {
                                    cursor: editing ? 'text' : 'default'
                                }
                            }}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                {!editing ? (
                    <>
                        <Button
                            onClick={() => setEditing(true)}
                            startIcon={<Edit />}
                            variant="contained"
                        >
                            Редактировать
                        </Button>
                        <Button onClick={handleClose}>
                            Закрыть
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            onClick={handleSave}
                            disabled={saving || note === originalNote}
                            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                            variant="contained"
                            color="primary"
                        >
                            {saving ? 'Сохранение...' : 'Сохранить'}
                        </Button>
                        <Button
                            onClick={handleCancel}
                            disabled={saving}
                            color="secondary"
                        >
                            Отмена
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default UserNoteDialog;
